import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Shield, User, MapPin, Heart, UtensilsCrossed,
  Activity, Thermometer, Clock, MessageSquare, Send
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const depLabels = {
  autovalido: "Autoválido",
  dependencia_media: "Dependencia Media",
  dependencia_total: "Dependencia Total",
};

export default function FamilyPortalPage() {
  const { accessCode } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [manualCode, setManualCode] = useState("");
  const [code, setCode] = useState(accessCode || "");
  const [messages, setMessages] = useState([]);
  const [msgContent, setMsgContent] = useState("");
  const [senderName, setSenderName] = useState("");
  const [sending, setSending] = useState(false);

  const fetchMessages = useCallback(async () => {
    if (!code) return;
    try {
      const r = await axios.get(`${BACKEND_URL}/api/family-portal/${code}/messages`);
      setMessages(r.data);
    } catch { /* ignore */ }
  }, [code]);

  useEffect(() => {
    if (!code) { setLoading(false); return; }
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/family-portal/${code}`)
      .then(r => { setData(r.data); setError(null); })
      .catch(() => setError("Código de acceso inválido o residente no encontrado"))
      .finally(() => setLoading(false));
    fetchMessages();
  }, [code, fetchMessages]);

  const handleSendMessage = async () => {
    if (!msgContent.trim() || !senderName.trim()) return;
    setSending(true);
    try {
      await axios.post(`${BACKEND_URL}/api/family-portal/${code}/messages`, {
        content: msgContent,
        sender_name: senderName
      });
      setMsgContent("");
      toast.success("Mensaje enviado");
      fetchMessages();
    } catch (err) {
      toast.error("Error al enviar el mensaje");
    }
    setSending(false);
  };

  if (!code && !accessCode) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center p-4" data-testid="family-portal-entry">
        <div className="w-full max-w-md animate-fade-in-up">
          <Card className="rounded-2xl shadow-sm border-slate-100">
            <CardContent className="p-8">
              <div className="flex flex-col items-center mb-6">
                <div className="w-14 h-14 rounded-xl bg-violet-600 flex items-center justify-center mb-3">
                  <Shield className="w-7 h-7 text-white" strokeWidth={1.8} />
                </div>
                <h1 className="text-xl font-bold text-slate-900 font-[Manrope]">Portal Familiar</h1>
                <p className="text-sm text-slate-400 text-center mt-1">Ingrese el código de acceso proporcionado por la residencia</p>
              </div>
              <div className="space-y-4">
                <Input
                  value={manualCode}
                  onChange={e => setManualCode(e.target.value.toUpperCase())}
                  placeholder="Código de acceso"
                  className="rounded-xl h-12 bg-slate-50 border-slate-200 text-center text-lg font-mono font-bold tracking-widest focus:border-violet-500"
                  data-testid="portal-code-input"
                />
                <Button
                  onClick={() => setCode(manualCode)}
                  disabled={!manualCode}
                  className="w-full rounded-xl h-11 bg-violet-600 text-white font-semibold hover:bg-violet-700 active:scale-[0.98] transition-all"
                  data-testid="portal-access-btn"
                >
                  Acceder
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (loading) return (
    <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center">
      <p className="text-slate-400">Cargando portal familiar...</p>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center p-4">
      <Card className="rounded-2xl shadow-sm border-slate-100 max-w-md w-full">
        <CardContent className="p-8 text-center">
          <p className="text-rose-600 font-semibold mb-2">{error}</p>
          <Button onClick={() => { setCode(""); setError(null); }} variant="outline" className="rounded-xl mt-4 border-slate-200" data-testid="portal-retry-btn">
            Intentar de nuevo
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  const { resident, daily_menu, activities, vitals } = data || {};

  return (
    <div className="min-h-screen bg-[#FAFAF8] p-4 lg:p-8" data-testid="family-portal-page">
      <div className="max-w-3xl mx-auto space-y-5 animate-fade-in-up">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-8 h-8 rounded-lg bg-violet-600 flex items-center justify-center">
            <Shield className="w-4 h-4 text-white" strokeWidth={2} />
          </div>
          <span className="font-bold text-slate-900 font-[Manrope]">Gerion</span>
          <Badge className="rounded-full bg-violet-50 text-violet-700 border-0 text-[10px] font-semibold ml-auto">Portal Familiar</Badge>
        </div>

        <Card className="rounded-2xl border-slate-100 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-violet-50 flex items-center justify-center text-violet-600 font-bold text-xl">
                {resident?.name?.charAt(0)}
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900 font-[Manrope]">{resident?.name}</h2>
                <div className="flex items-center gap-3 mt-1 flex-wrap">
                  {resident?.room_number && <span className="text-sm text-slate-400 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Hab. {resident?.room_number}</span>}
                  <span className="text-sm text-slate-400 flex items-center gap-1"><Heart className="w-3.5 h-3.5" /> {resident?.obra_social}</span>
                  <Badge className="rounded-full bg-slate-100 text-slate-600 border-0 text-xs font-medium">{depLabels[resident?.dependency_level] || resident?.dependency_level}</Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-slate-100 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                <UtensilsCrossed className="w-5 h-5 text-amber-600" strokeWidth={1.8} />
              </div>
              <h3 className="font-semibold text-slate-900 text-sm">Menú del Día</h3>
            </div>
            {daily_menu ? (
              <div className="space-y-2">
                {Object.entries(daily_menu).map(([meal, desc]) => (
                  <div key={meal} className="bg-slate-50 rounded-xl px-4 py-3">
                    <p className="text-[10px] text-slate-400 uppercase tracking-widest font-semibold">{meal}</p>
                    <p className="text-sm text-slate-700 mt-0.5">{desc}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400">Menú aún no disponible para hoy</p>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-slate-100 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                <Activity className="w-5 h-5 text-emerald-600" strokeWidth={1.8} />
              </div>
              <h3 className="font-semibold text-slate-900 text-sm">Actividades del Día</h3>
            </div>
            {activities && activities.length > 0 ? (
              <div className="space-y-2">
                {activities.map((act, i) => (
                  <div key={i} className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3">
                    <Clock className="w-4 h-4 text-slate-300 flex-shrink-0" />
                    <p className="text-sm text-slate-700">{act}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400">No hay actividades programadas para hoy</p>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-slate-100 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-fuchsia-50 flex items-center justify-center">
                <Thermometer className="w-5 h-5 text-fuchsia-600" strokeWidth={1.8} />
              </div>
              <h3 className="font-semibold text-slate-900 text-sm">Signos Vitales Recientes</h3>
            </div>
            {vitals && vitals.length > 0 ? (
              <div className="space-y-3">
                {vitals.slice(0, 3).map((v, i) => (
                  <div key={i} className="bg-slate-50 rounded-xl p-4">
                    <p className="text-xs text-slate-400 mb-2">{new Date(v.created_at).toLocaleString("es-AR")}</p>
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                      {[
                        { label: "TA", value: v.blood_pressure },
                        { label: "FC", value: v.heart_rate ? `${v.heart_rate}` : null },
                        { label: "Temp", value: v.temperature ? `${v.temperature}°` : null },
                        { label: "SpO2", value: v.oxygen_saturation ? `${v.oxygen_saturation}%` : null },
                        { label: "Peso", value: v.weight ? `${v.weight}kg` : null },
                      ].map(item => item.value && (
                        <div key={item.label} className="text-center">
                          <p className="text-[10px] text-slate-400 font-semibold uppercase">{item.label}</p>
                          <p className="text-sm font-semibold text-slate-700">{item.value}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400">Sin registros recientes</p>
            )}
          </CardContent>
        </Card>

        {/* Messaging */}
        <Card className="rounded-2xl border-slate-100 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-violet-600" strokeWidth={1.8} />
              </div>
              <h3 className="font-semibold text-slate-900 text-sm">Mensajes</h3>
            </div>

            {/* Message history */}
            <div className="space-y-3 max-h-[300px] overflow-y-auto mb-4">
              {messages.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">No hay mensajes aún. Envíe su primera consulta.</p>
              ) : (
                messages.map(msg => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.sender_type === "family" ? "justify-end" : "justify-start"}`}
                    data-testid={`portal-msg-${msg.id}`}
                  >
                    <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      msg.sender_type === "family" ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-900"
                    }`}>
                      <p className={`text-[10px] font-semibold mb-1 ${msg.sender_type === "family" ? "text-violet-200" : "text-slate-400"}`}>
                        {msg.sender_name}
                      </p>
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                      <p className={`text-[10px] mt-1 ${msg.sender_type === "family" ? "text-violet-300" : "text-slate-400"}`}>
                        {new Date(msg.created_at).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Send message */}
            <div className="border-t border-slate-100 pt-4 space-y-3">
              {!senderName && (
                <div>
                  <Input
                    value={senderName}
                    onChange={e => setSenderName(e.target.value)}
                    placeholder="Su nombre"
                    className="rounded-xl h-10 bg-slate-50 border-slate-200"
                    data-testid="portal-sender-name"
                  />
                </div>
              )}
              {senderName && (
                <p className="text-xs text-slate-400">Enviando como: <span className="font-medium text-slate-600">{senderName}</span></p>
              )}
              <div className="flex gap-2 items-end">
                <Textarea
                  value={msgContent}
                  onChange={e => setMsgContent(e.target.value)}
                  placeholder="Escribir mensaje o consulta..."
                  className="rounded-xl bg-slate-50 min-h-[50px] border-slate-200 flex-1 resize-none text-sm"
                  data-testid="portal-msg-input"
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={!msgContent.trim() || !senderName.trim() || sending}
                  className="rounded-xl h-10 px-4 bg-violet-600 text-white hover:bg-violet-700 active:scale-[0.98] transition-all"
                  data-testid="portal-send-btn"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-400 pb-8">Gerion — Sistema de Gestión Geriátrica</p>
      </div>
    </div>
  );
}
